/**
 * useStream —— SSE 实时通道（EventSource）→ 写入 react-query 缓存。
 * 这是「开两个浏览器窗口，A 点开关 → B 卡片同步变化」的实现核心：
 * 服务端每次 ingest 都会推 device.state / device.online，前端把它合并进设备缓存。
 * 另外消费 mcp.voice：小智语音的连接状态与调用次数实时刷新 MCP 管理页。
 */
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Device, StreamEvent } from '@znjj/shared';
import { tokenStore } from '../api/auth';
import { APP_BASE } from '../api/client';
import { deviceKeys, type DeviceList } from '../api/devices';
import { mcpKeys, type VoiceBinding } from '../api/mcp';

function patchDevice(device: Device, event: StreamEvent): Device {
  if (event.type === 'device.state') {
    return { ...device, state: { ...device.state, ...event.patch } };
  }
  if (event.type === 'device.online') {
    return {
      ...device,
      online: event.online,
      rssi: event.rssi,
      lastSeenAt: event.at,
    };
  }
  return device;
}

/** mcp.voice → 绑定缓存：状态与计数实时增长，「连接于 …」用事件时间回填 */
function patchVoice(binding: VoiceBinding, event: StreamEvent): VoiceBinding {
  if (event.type !== 'mcp.voice') return binding;
  return {
    ...binding,
    status: event.status,
    callCount: event.callCount,
    connectedAt: event.status === 'connected' ? event.at : binding.connectedAt,
    disconnectedAt: event.status === 'disconnected' ? event.at : binding.disconnectedAt,
    lastCallAt: event.callCount > binding.callCount ? event.at : binding.lastCallAt,
  };
}

export function useStream(enabled: boolean): void {
  const qc = useQueryClient();

  useEffect(() => {
    if (!enabled) return;
    const token = tokenStore.get();
    if (!token) return;

    const es = new EventSource(`${APP_BASE}/api/stream?token=${encodeURIComponent(token)}`);

    const parse = (evt: MessageEvent): StreamEvent | null => {
      try {
        return JSON.parse(evt.data as string) as StreamEvent;
      } catch {
        return null;
      }
    };

    const onDevice = (evt: MessageEvent): void => {
      const event = parse(evt);
      // 遥测事件频次高，设备墙不消费（状态已随 device.state 一起更新）
      if (!event || (event.type !== 'device.state' && event.type !== 'device.online')) return;

      qc.setQueryData<DeviceList>(deviceKeys.list, (prev) => {
        if (!prev) return prev;
        const list = prev.list.map((d) => (d.id === event.deviceId ? patchDevice(d, event) : d));
        return {
          ...prev,
          list,
          online: list.filter((d) => d.online).length,
        };
      });
    };

    const onVoice = (evt: MessageEvent): void => {
      const event = parse(evt);
      if (!event || event.type !== 'mcp.voice') return;
      qc.setQueryData<{ binding: VoiceBinding }>(mcpKeys.voice, (prev) =>
        prev ? { binding: patchVoice(prev.binding, event) } : prev,
      );
    };

    es.addEventListener('device.state', onDevice);
    es.addEventListener('device.online', onDevice);
    es.addEventListener('mcp.voice', onVoice);

    return () => {
      es.removeEventListener('device.state', onDevice);
      es.removeEventListener('device.online', onDevice);
      es.removeEventListener('mcp.voice', onVoice);
      es.close();
    };
  }, [enabled, qc]);
}
